"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState, useMemo, Suspense } from "react";
import dynamic from "next/dynamic";
import { GERMAN_ONLY_PAGES, removeLocaleFromPath } from "@/app/lib/i18n-config";

// Public routes that don't need auth providers
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/register",
  "/open-positions",
  "/home",
  "/home/pricing",
  "/privacy-policy",
  "/imprint",
  "/datenschutz",
  "/impressum",
  "/job",
  "/upload",
  "/api/submit-lead",
  "/auth-required",
  "/verify",
  "/reviews",
  "/forgot-password",
  "/reset-password",
  "/blog",
  "/resources",
  "/market-check",
  "/dashboard/job/preview",
  "/create-your-account",
  "/about-us",
  "/free-ats",
  "/career-page",
  ...GERMAN_ONLY_PAGES,
  "/companies",
];

// Import SessionProvider normally since HomeNavbar and other components use useSession
// But dynamically import the heavier auth components only when needed
import SessionProvider from "./SessionProvider";
const SessionValidator = dynamic(() => import("./SessionValidator"), {
  ssr: false,
});
const AuthGuard = dynamic(() => import("./AuthGuard"), {
  ssr: false,
});

interface ConditionalAuthProvidersProps {
  children: React.ReactNode;
}

export default function ConditionalAuthProviders({
  children,
}: ConditionalAuthProvidersProps) {
  const pathname = usePathname();
  const [needsAuth, setNeedsAuth] = useState<boolean | null>(null);
  const [isClient, setIsClient] = useState(false);

  // Strip locale prefix for route checking
  const cleanPathname = useMemo(
    () => removeLocaleFromPath(pathname || "/"),
    [pathname]
  );

  useEffect(() => {
    setIsClient(true);
    // Check if current route needs auth (using locale-stripped pathname)
    const isPublicRoute = PUBLIC_ROUTES.some(
      (route) => cleanPathname === route || cleanPathname.startsWith(route + "/")
    );
    setNeedsAuth(!isPublicRoute);
  }, [cleanPathname]);

  // During SSR or initial client render, provide SessionProvider for components that need it
  // We'll determine if we need the heavier auth components once client-side routing is ready
  if (!isClient || needsAuth === null) {
    return <SessionProvider>{children}</SessionProvider>;
  }

  // Always provide SessionProvider since some components (like HomeNavbar) use useSession
  // But only load the heavier SessionValidator and AuthGuard on protected routes
  if (!needsAuth) {
    // On public routes, only provide SessionProvider (lightweight)
    return <SessionProvider>{children}</SessionProvider>;
  }

  // On protected routes, dynamically load the heavier auth components
  return (
    <SessionProvider>
      <Suspense fallback={null}>
        <SessionValidator>
          <AuthGuard>{children}</AuthGuard>
        </SessionValidator>
      </Suspense>
    </SessionProvider>
  );
}

